import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { brandsAPI, integrationsAPI } from '../utils/api';
import { toast } from 'sonner';
import { CheckCircle2, ArrowRight, ArrowLeft, Sparkles, Building2, Globe, Target, Share2, Bot, Link as LinkIcon } from 'lucide-react';
import GoogleOAuthButton from '../components/GoogleOAuthButton';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const OnboardingWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [brandId, setBrandId] = useState(null);
  
  // Check if this is white label client brand creation
  const isClientBrand = new URLSearchParams(window.location.search).get('mode') === 'client';
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isWhiteLabel = user.role === 'agency_admin' || user.role === 'AGENCY_ADMIN';

  // Step 1: Brand Information
  const [brandData, setBrandData] = useState({
    name: '',
    website: '',
    industry: '',
    description: '',
    target_audience: '',
    brand_voice: '',
    locations: []
  });

  // Step 2: Business Goals
  const [businessGoals, setBusinessGoals] = useState({
    primary_goals: [],
    target_keywords: [],
    competitors: [],
    unique_selling_points: ''
  });

  // Step 3: Content Preferences
  const [contentPreferences, setContentPreferences] = useState({
    content_types: [],
    posting_frequency: 'daily',
    tone: 'professional',
    topics: [],
    avoid_topics: []
  });

  // Step 4: Google OAuth Integrations (NEW)
  const [googleOAuth, setGoogleOAuth] = useState({
    search_console: false,
    analytics: false,
    business_profile: false
  });

  // Step 5: Social Media (previously Step 4)
  const [socialMedia, setSocialMedia] = useState({
    platforms: [],
    instagram_username: '',
    facebook_page: '',
    linkedin_page: '',
    twitter_handle: ''
  });

  // Step 6: SEO Settings (previously Step 5)
  const [seoSettings, setSeoSettings] = useState({
    focus_keywords: [],
    location_targeting: '',
    language: 'en',
    meta_description: ''
  });

  // Step 7: Website & Hosting Access (previously Step 6)
  const [hostingDetails, setHostingDetails] = useState({
    hosting_provider: '',
    cpanel_url: '',
    cpanel_username: '',
    cpanel_password: '',
    ftp_host: '',
    ftp_username: '',
    ftp_password: '',
    ftp_port: '21'
  });

  // Step 8: Google My Business (previously Step 7)
  const [gmbDetails, setGmbDetails] = useState({
    has_gmb: '',
    gmb_email: '',
    gmb_password: '',
    business_name: '',
    gmb_location_id: ''
  });

  // Step 9: Social Media Credentials (previously Step 8)
  const [socialCredentials, setSocialCredentials] = useState({
    instagram_username: '',
    instagram_password: '',
    facebook_email: '',
    facebook_password: '',
    facebook_page_id: '',
    linkedin_email: '',
    linkedin_password: '',
    twitter_email: '',
    twitter_password: ''
  });

  const totalSteps = 9;  // Updated from 8 to 9
  const progress = (currentStep / totalSteps) * 100;

  const industries = [
    'Technology', 'Healthcare', 'Finance', 'Retail', 'Education',
    'Real Estate', 'Food & Beverage', 'Travel', 'Fashion', 'Other'
  ];

  const goals = [
    'Increase Brand Awareness',
    'Generate Leads',
    'Drive Website Traffic',
    'Boost Sales',
    'Build Community',
    'Improve SEO Rankings'
  ];

  const contentTypes = [
    'Blog Posts',
    'Social Media Posts',
    'Infographics',
    'Videos',
    'Case Studies',
    'Product Updates'
  ];

  const tones = [
    'Professional',
    'Casual',
    'Friendly',
    'Authoritative',
    'Humorous',
    'Inspirational'
  ];

  const platforms = [
    'Instagram',
    'Facebook',
    'LinkedIn',
    'Twitter',
    'Pinterest',
    'YouTube'
  ];

  // Handle Step 1: Brand Information
  const handleStep1Submit = async () => {
    if (!brandData.name || !brandData.website || !brandData.industry) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await brandsAPI.create(brandData);
      if (response.data.success) {
        setBrandId(response.data.brand.id);
        toast.success('Brand information saved!');
        setCurrentStep(2);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save brand information');
    } finally {
      setLoading(false);
    }
  };

  // Handle Step 2: Business Goals
  const handleStep2Submit = async () => {
    if (businessGoals.primary_goals.length === 0) {
      toast.error('Please select at least one primary goal');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(
        `${API_BASE_URL}/api/brands/${brandId}`,
        { business_goals: businessGoals },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Business goals saved!');
      setCurrentStep(3);
    } catch (error) {
      toast.error('Failed to save business goals');
    } finally {
      setLoading(false);
    }
  };

  // Handle Step 3: Content Preferences
  const handleStep3Submit = async () => {
    if (contentPreferences.content_types.length === 0) {
      toast.error('Please select at least one content type');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(
        `${API_BASE_URL}/api/brands/${brandId}`,
        { 
          content_preferences: contentPreferences,
          automation: {
            enabled: true,
            frequency: contentPreferences.posting_frequency
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Content preferences saved!');
      setCurrentStep(4);
    } catch (error) {
      toast.error('Failed to save content preferences');
    } finally {
      setLoading(false);
    }
  };

  // Handle Step 4: Google OAuth Integrations (NEW - Can skip)
  const handleStep4Submit = async () => {
    // Check OAuth status
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(
        `${API_BASE_URL}/api/oauth/google/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const status = response.data.status;
      setGoogleOAuth({
        search_console: status.search_console?.connected || false,
        analytics: status.analytics?.connected || false,
        business_profile: status.business_profile?.connected || false
      });
      
      // Save OAuth status to brand (optional)
      await axios.put(
        `${API_BASE_URL}/api/brands/${brandId}`,
        { google_integrations: googleOAuth },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Google integrations configured!');
      setCurrentStep(5);
    } catch (error) {
      console.error('Error checking OAuth status:', error);
      // Allow to continue even if status check fails
      setCurrentStep(5);
    }
  };

  // Handle Step 5: Social Media (Previously Step 4) (Skip option available)
  const handleStep5Submit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(
        `${API_BASE_URL}/api/brands/${brandId}`,
        { social_media: socialMedia },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Social media information saved!');
      setCurrentStep(6);
    } catch (error) {
      toast.error('Failed to save social media information');
    } finally {
      setLoading(false);
    }
  };

  // Handle Step 6: SEO Settings (Previously Step 5)
  const handleStep6Submit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      // Save SEO settings
      await axios.put(
        `${API_BASE_URL}/api/brands/${brandId}`,
        { seo_settings: seoSettings },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('SEO settings saved!');
      setCurrentStep(7);
    } catch (error) {
      toast.error('Failed to save SEO settings');
    } finally {
      setLoading(false);
    }
  };

  // Handle Step 7: Website & Hosting Access (Previously Step 6)
  const handleStep7Submit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      // Save hosting details securely
      await axios.put(
        `${API_BASE_URL}/api/brands/${brandId}`,
        { hosting_details: hostingDetails },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Website access details saved securely!');
      setCurrentStep(8);
    } catch (error) {
      toast.error('Failed to save hosting details');
    } finally {
      setLoading(false);
    }
  };

  // Handle Step 8: Google My Business (Previously Step 7)
  const handleStep8Submit = async () => {
    if (gmbDetails.has_gmb === 'yes' && (!gmbDetails.gmb_email || !gmbDetails.gmb_password)) {
      toast.error('Please provide GMB credentials or select "No"');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      // Save GMB details securely
      await axios.put(
        `${API_BASE_URL}/api/brands/${brandId}`,
        { gmb_details: gmbDetails },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Google My Business details saved!');
      setCurrentStep(9);
    } catch (error) {
      toast.error('Failed to save GMB details');
    } finally {
      setLoading(false);
    }
  };

  // Handle Step 9: Social Media Credentials & Complete (Previously Step 8)
  const handleStep9Submit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      // Save social media credentials securely
      await axios.put(
        `${API_BASE_URL}/api/brands/${brandId}`,
        { social_credentials: socialCredentials },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Activate AI Agent after all details are collected
      const activationResponse = await axios.post(
        `${API_BASE_URL}/api/brands/${brandId}/activate-ai-agent`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (activationResponse.data.success) {
        toast.success('🎉 Setup Complete! AI Agent is now active and working!', {
          duration: 5000
        });
        
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error) {
      toast.error('Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  const toggleArrayItem = (array, item, setFunction) => {
    if (array.includes(item)) {
      setFunction(array.filter(i => i !== item));
    } else {
      setFunction([...array, item]);
    }
  };

  // Handle OAuth status change
  const handleOAuthStatusChange = (service, connected) => {
    setGoogleOAuth(prev => ({
      ...prev,
      [service]: connected
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {isClientBrand ? 'Create Client Brand' : 'Welcome to AutomateSEO.ai'}
            </h1>
          </div>
          <p className="text-gray-600">
            {isClientBrand 
              ? 'Set up complete brand automation for your client in 9 comprehensive steps'
              : 'Let\'s set up your AI-powered automation with complete integration in 9 steps'}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Step {currentStep} of {totalSteps}</span>
            <span className="text-sm font-medium text-gray-700">{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-3" />
        </div>

        {/* Step Indicator */}
        <div className="flex justify-between mb-8">
          {[1, 2, 3, 4, 5].map((step) => (
            <div key={step} className="flex flex-col items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                step < currentStep ? 'bg-green-500 text-white' :
                step === currentStep ? 'bg-blue-600 text-white' :
                'bg-gray-200 text-gray-500'
              }`}>
                {step < currentStep ? <CheckCircle2 className="w-6 h-6" /> : step}
              </div>
              <span className="text-xs mt-2 text-center text-gray-600">
                {step === 1 && 'Brand'}
                {step === 2 && 'Goals'}
                {step === 3 && 'Content'}
                {step === 4 && 'Google'}
                {step === 5 && 'Social'}
              </span>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card className="card">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              {currentStep === 1 && <><Building2 className="w-6 h-6 text-blue-600" /> Brand Information</>}
              {currentStep === 2 && <><Target className="w-6 h-6 text-blue-600" /> Business Goals</>}
              {currentStep === 3 && <><Globe className="w-6 h-6 text-blue-600" /> Content Preferences</>}
              {currentStep === 4 && <><LinkIcon className="w-6 h-6 text-blue-600" /> Google Integrations</>}
              {currentStep === 5 && <><Share2 className="w-6 h-6 text-blue-600" /> Social Media</>}
              {currentStep >= 6 && <><Bot className="w-6 h-6 text-blue-600" /> Advanced Setup</>}
            </CardTitle>
            <CardDescription>
              {currentStep === 1 && 'Tell us about your brand and business'}
              {currentStep === 2 && 'What are your primary business objectives?'}
              {currentStep === 3 && 'Define your content strategy and preferences'}
              {currentStep === 4 && 'Connect Google Search Console, Analytics & Business Profile (optional)'}
              {currentStep === 5 && 'Connect your social media accounts (optional)'}
              {currentStep >= 6 && 'Complete advanced setup and activate AI'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Step 1: Brand Information */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="brand_name">Brand Name *</Label>
                  <Input
                    id="brand_name"
                    placeholder="e.g., Acme Corp"
                    value={brandData.name}
                    onChange={(e) => setBrandData({...brandData, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="website">Website URL *</Label>
                  <Input
                    id="website"
                    placeholder="https://example.com"
                    value={brandData.website}
                    onChange={(e) => setBrandData({...brandData, website: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="industry">Industry *</Label>
                  <Select value={brandData.industry} onValueChange={(value) => setBrandData({...brandData, industry: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((ind) => (
                        <SelectItem key={ind} value={ind.toLowerCase()}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="description">Brand Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe your brand, what you do, and what makes you unique..."
                    value={brandData.description}
                    onChange={(e) => setBrandData({...brandData, description: e.target.value})}
                    rows={4}
                  />
                </div>
                <div>
                  <Label htmlFor="target_audience">Target Audience</Label>
                  <Input
                    id="target_audience"
                    placeholder="e.g., Small business owners, Tech enthusiasts"
                    value={brandData.target_audience}
                    onChange={(e) => setBrandData({...brandData, target_audience: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="brand_voice">Brand Voice</Label>
                  <Input
                    id="brand_voice"
                    placeholder="e.g., Professional yet approachable"
                    value={brandData.brand_voice}
                    onChange={(e) => setBrandData({...brandData, brand_voice: e.target.value})}
                  />
                </div>
              </div>
            )}

            {/* Step 2: Business Goals */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div>
                  <Label>Primary Goals * (Select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {goals.map((goal) => (
                      <button
                        key={goal}
                        type="button"
                        onClick={() => toggleArrayItem(
                          businessGoals.primary_goals,
                          goal,
                          (newGoals) => setBusinessGoals({...businessGoals, primary_goals: newGoals})
                        )}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          businessGoals.primary_goals.includes(goal)
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        {goal}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="keywords">Target Keywords (comma separated)</Label>
                  <Input
                    id="keywords"
                    placeholder="e.g., SEO automation, content marketing, AI tools"
                    onChange={(e) => setBusinessGoals({
                      ...businessGoals,
                      target_keywords: e.target.value.split(',').map(k => k.trim())
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="competitors">Competitors (comma separated)</Label>
                  <Input
                    id="competitors"
                    placeholder="e.g., competitor1.com, competitor2.com"
                    onChange={(e) => setBusinessGoals({
                      ...businessGoals,
                      competitors: e.target.value.split(',').map(c => c.trim())
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="usp">Unique Selling Points</Label>
                  <Textarea
                    id="usp"
                    placeholder="What makes your brand different from competitors?"
                    value={businessGoals.unique_selling_points}
                    onChange={(e) => setBusinessGoals({...businessGoals, unique_selling_points: e.target.value})}
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Step 3: Content Preferences */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div>
                  <Label>Content Types * (Select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {contentTypes.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleArrayItem(
                          contentPreferences.content_types,
                          type,
                          (newTypes) => setContentPreferences({...contentPreferences, content_types: newTypes})
                        )}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          contentPreferences.content_types.includes(type)
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="frequency">Posting Frequency</Label>
                  <Select 
                    value={contentPreferences.posting_frequency}
                    onValueChange={(value) => setContentPreferences({...contentPreferences, posting_frequency: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="3x_week">3x per week</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Content Tone</Label>
                  <Select 
                    value={contentPreferences.tone}
                    onValueChange={(value) => setContentPreferences({...contentPreferences, tone: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tones.map((tone) => (
                        <SelectItem key={tone} value={tone.toLowerCase()}>{tone}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="topics">Preferred Topics (comma separated)</Label>
                  <Input
                    id="topics"
                    placeholder="e.g., Industry news, Tips & tricks, Case studies"
                    onChange={(e) => setContentPreferences({
                      ...contentPreferences,
                      topics: e.target.value.split(',').map(t => t.trim())
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="avoid">Topics to Avoid (comma separated)</Label>
                  <Input
                    id="avoid"
                    placeholder="e.g., Politics, Religion"
                    onChange={(e) => setContentPreferences({
                      ...contentPreferences,
                      avoid_topics: e.target.value.split(',').map(t => t.trim())
                    })}
                  />
                </div>
              </div>
            )}

            {/* Step 4: Google OAuth Integrations (NEW) */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg mb-6 border border-blue-200">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-white rounded-lg">
                      <svg className="w-8 h-8" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Connect Your Google Services</h3>
                      <p className="text-sm text-gray-700 mb-2">
                        Enable real-time data fetching from your Google accounts for comprehensive SEO and analytics automation.
                      </p>
                      <p className="text-xs text-gray-600">
                        <strong>Optional:</strong> You can skip this step and connect later from the Integrations page. Connecting now will enable immediate data-driven insights.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <GoogleOAuthButton
                    service="search_console"
                    serviceName="Google Search Console"
                    icon={
                      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                        <rect width="24" height="24" rx="4" fill="#4285F4"/>
                        <path d="M7 10h10M7 14h7" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    }
                    description="Monitor your website's search performance, track keywords, and identify indexing issues automatically."
                    connected={googleOAuth.search_console}
                    onStatusChange={handleOAuthStatusChange}
                  />

                  <GoogleOAuthButton
                    service="analytics"
                    serviceName="Google Analytics 4"
                    icon={
                      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                        <rect width="24" height="24" rx="4" fill="#FF6D01"/>
                        <path d="M6 18V12M12 18V8M18 18V14" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    }
                    description="Access traffic data, user behavior insights, and conversion metrics for data-driven decisions."
                    connected={googleOAuth.analytics}
                    onStatusChange={handleOAuthStatusChange}
                  />

                  <GoogleOAuthButton
                    service="business_profile"
                    serviceName="Google Business Profile"
                    icon={
                      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                        <rect width="24" height="24" rx="4" fill="#34A853"/>
                        <path d="M12 8V16M8 12H16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    }
                    description="Manage your business listings, respond to reviews, and track local SEO performance effortlessly."
                    connected={googleOAuth.business_profile}
                    onStatusChange={handleOAuthStatusChange}
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-900 mb-1">What happens when you connect?</p>
                      <ul className="text-xs text-yellow-800 space-y-1">
                        <li>• You'll be redirected to Google to authorize access</li>
                        <li>• We'll securely store your encrypted access tokens</li>
                        <li>• AutomateSEO will fetch real-time data from your accounts</li>
                        <li>• You can disconnect anytime from the Integrations page</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Social Media (Previously Step 4) */}
            {currentStep === 5 && (
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Optional:</strong> Connect your social media accounts now, or skip and add them later from the Integrations page.
                  </p>
                </div>
                <div>
                  <Label>Social Media Platforms</Label>
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    {platforms.map((platform) => (
                      <button
                        key={platform}
                        type="button"
                        onClick={() => toggleArrayItem(
                          socialMedia.platforms,
                          platform,
                          (newPlatforms) => setSocialMedia({...socialMedia, platforms: newPlatforms})
                        )}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          socialMedia.platforms.includes(platform)
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        {platform}
                      </button>
                    ))}
                  </div>
                </div>
                {socialMedia.platforms.includes('Instagram') && (
                  <div>
                    <Label htmlFor="instagram">Instagram Username</Label>
                    <Input
                      id="instagram"
                      placeholder="@yourbrand"
                      value={socialMedia.instagram_username}
                      onChange={(e) => setSocialMedia({...socialMedia, instagram_username: e.target.value})}
                    />
                  </div>
                )}
                {socialMedia.platforms.includes('Facebook') && (
                  <div>
                    <Label htmlFor="facebook">Facebook Page URL</Label>
                    <Input
                      id="facebook"
                      placeholder="https://facebook.com/yourpage"
                      value={socialMedia.facebook_page}
                      onChange={(e) => setSocialMedia({...socialMedia, facebook_page: e.target.value})}
                    />
                  </div>
                )}
                {socialMedia.platforms.includes('LinkedIn') && (
                  <div>
                    <Label htmlFor="linkedin">LinkedIn Company Page URL</Label>
                    <Input
                      id="linkedin"
                      placeholder="https://linkedin.com/company/yourcompany"
                      value={socialMedia.linkedin_page}
                      onChange={(e) => setSocialMedia({...socialMedia, linkedin_page: e.target.value})}
                    />
                  </div>
                )}
                {socialMedia.platforms.includes('Twitter') && (
                  <div>
                    <Label htmlFor="twitter">Twitter Handle</Label>
                    <Input
                      id="twitter"
                      placeholder="@yourbrand"
                      value={socialMedia.twitter_handle}
                      onChange={(e) => setSocialMedia({...socialMedia, twitter_handle: e.target.value})}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 6: SEO Settings (Previously Step 5) */}
            {currentStep === 6 && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg mb-4">
                  <p className="text-sm font-medium text-blue-900">
                    Let's optimize your SEO settings for better search rankings!
                  </p>
                </div>
                <div>
                  <Label htmlFor="focus_keywords">Focus Keywords (comma separated)</Label>
                  <Input
                    id="focus_keywords"
                    placeholder="e.g., best SEO tools, marketing automation"
                    onChange={(e) => setSeoSettings({
                      ...seoSettings,
                      focus_keywords: e.target.value.split(',').map(k => k.trim())
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location Targeting</Label>
                  <Input
                    id="location"
                    placeholder="e.g., United States, Global"
                    value={seoSettings.location_targeting}
                    onChange={(e) => setSeoSettings({...seoSettings, location_targeting: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="language">Primary Language</Label>
                  <Select 
                    value={seoSettings.language}
                    onValueChange={(value) => setSeoSettings({...seoSettings, language: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="de">German</SelectItem>
                      <SelectItem value="hi">Hindi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="meta">Default Meta Description</Label>
                  <Textarea
                    id="meta"
                    placeholder="A brief description of your business for search engines..."
                    value={seoSettings.meta_description}
                    onChange={(e) => setSeoSettings({...seoSettings, meta_description: e.target.value})}
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Step 7: Website & Hosting Access (Previously Step 6) */}
            {currentStep === 7 && (
              <div className="space-y-4">
                <div className="bg-orange-50 p-4 rounded-lg mb-4 border border-orange-200">
                  <p className="text-sm font-medium text-orange-900 mb-2">
                    🔒 Secure Information Required
                  </p>
                  <p className="text-xs text-orange-700">
                    We need your hosting/cPanel credentials to automate SEO updates on your website. All credentials are encrypted and stored securely.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hosting_provider">Hosting Provider</Label>
                    <Input
                      id="hosting_provider"
                      placeholder="e.g., Hostinger, GoDaddy, Bluehost"
                      value={hostingDetails.hosting_provider}
                      onChange={(e) => setHostingDetails({...hostingDetails, hosting_provider: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="cpanel_url">cPanel URL</Label>
                    <Input
                      id="cpanel_url"
                      placeholder="e.g., cpanel.yourdomain.com"
                      value={hostingDetails.cpanel_url}
                      onChange={(e) => setHostingDetails({...hostingDetails, cpanel_url: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cpanel_username">cPanel Username *</Label>
                    <Input
                      id="cpanel_username"
                      placeholder="Your cPanel username"
                      value={hostingDetails.cpanel_username}
                      onChange={(e) => setHostingDetails({...hostingDetails, cpanel_username: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="cpanel_password">cPanel Password *</Label>
                    <Input
                      id="cpanel_password"
                      type="password"
                      placeholder="Your cPanel password"
                      value={hostingDetails.cpanel_password}
                      onChange={(e) => setHostingDetails({...hostingDetails, cpanel_password: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">FTP Access (Optional)</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="ftp_host">FTP Host</Label>
                      <Input
                        id="ftp_host"
                        placeholder="ftp.yourdomain.com"
                        value={hostingDetails.ftp_host}
                        onChange={(e) => setHostingDetails({...hostingDetails, ftp_host: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="ftp_username">FTP Username</Label>
                      <Input
                        id="ftp_username"
                        placeholder="FTP username"
                        value={hostingDetails.ftp_username}
                        onChange={(e) => setHostingDetails({...hostingDetails, ftp_username: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="ftp_password">FTP Password</Label>
                      <Input
                        id="ftp_password"
                        type="password"
                        placeholder="FTP password"
                        value={hostingDetails.ftp_password}
                        onChange={(e) => setHostingDetails({...hostingDetails, ftp_password: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 8: Google My Business (Previously Step 7) */}
            {currentStep === 8 && (
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg mb-4 border border-green-200">
                  <p className="text-sm font-medium text-green-900 mb-2">
                    📍 Google My Business Integration
                  </p>
                  <p className="text-xs text-green-700">
                    Connect your Google My Business account to automate local SEO, reviews, and posts.
                  </p>
                </div>
                <div>
                  <Label>Do you have a Google My Business account? *</Label>
                  <div className="flex gap-4 mt-2">
                    <button
                      type="button"
                      onClick={() => setGmbDetails({...gmbDetails, has_gmb: 'yes'})}
                      className={`flex-1 p-4 rounded-lg border-2 font-medium transition-all ${
                        gmbDetails.has_gmb === 'yes'
                          ? 'border-green-600 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                    >
                      ✓ Yes, I have GMB
                    </button>
                    <button
                      type="button"
                      onClick={() => setGmbDetails({...gmbDetails, has_gmb: 'no'})}
                      className={`flex-1 p-4 rounded-lg border-2 font-medium transition-all ${
                        gmbDetails.has_gmb === 'no'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      ✗ No, I don't have GMB
                    </button>
                  </div>
                </div>
                {gmbDetails.has_gmb === 'yes' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="gmb_email">GMB Email/Google Account *</Label>
                        <Input
                          id="gmb_email"
                          type="email"
                          placeholder="your-email@gmail.com"
                          value={gmbDetails.gmb_email}
                          onChange={(e) => setGmbDetails({...gmbDetails, gmb_email: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="gmb_password">GMB Password *</Label>
                        <Input
                          id="gmb_password"
                          type="password"
                          placeholder="Your Google password"
                          value={gmbDetails.gmb_password}
                          onChange={(e) => setGmbDetails({...gmbDetails, gmb_password: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="business_name">Business Name on GMB</Label>
                      <Input
                        id="business_name"
                        placeholder="Your business name as shown on Google"
                        value={gmbDetails.business_name}
                        onChange={(e) => setGmbDetails({...gmbDetails, business_name: e.target.value})}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 9: Social Media Credentials (Previously Step 8) */}
            {currentStep === 9 && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg mb-4 border border-purple-200">
                  <p className="text-sm font-medium text-purple-900 mb-2">
                    🎉 Final Step - Social Media Automation
                  </p>
                  <p className="text-xs text-purple-700">
                    Provide credentials for platforms you want to automate. Skip any platform you don't want to connect now.
                  </p>
                </div>
                
                {/* Instagram */}
                <div className="border rounded-lg p-4 bg-gradient-to-r from-pink-50 to-purple-50">
                  <p className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-2xl">📸</span> Instagram (Optional)
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ig_username">Instagram Username</Label>
                      <Input
                        id="ig_username"
                        placeholder="@yourbrand"
                        value={socialCredentials.instagram_username}
                        onChange={(e) => setSocialCredentials({...socialCredentials, instagram_username: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="ig_password">Instagram Password</Label>
                      <Input
                        id="ig_password"
                        type="password"
                        placeholder="Your Instagram password"
                        value={socialCredentials.instagram_password}
                        onChange={(e) => setSocialCredentials({...socialCredentials, instagram_password: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Facebook */}
                <div className="border rounded-lg p-4 bg-blue-50">
                  <p className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-2xl">👥</span> Facebook (Optional)
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fb_email">Facebook Email</Label>
                      <Input
                        id="fb_email"
                        type="email"
                        placeholder="your-email@gmail.com"
                        value={socialCredentials.facebook_email}
                        onChange={(e) => setSocialCredentials({...socialCredentials, facebook_email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="fb_password">Facebook Password</Label>
                      <Input
                        id="fb_password"
                        type="password"
                        placeholder="Your Facebook password"
                        value={socialCredentials.facebook_password}
                        onChange={(e) => setSocialCredentials({...socialCredentials, facebook_password: e.target.value})}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="fb_page_id">Facebook Page ID (Optional)</Label>
                      <Input
                        id="fb_page_id"
                        placeholder="Your Facebook page ID"
                        value={socialCredentials.facebook_page_id}
                        onChange={(e) => setSocialCredentials({...socialCredentials, facebook_page_id: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* LinkedIn */}
                <div className="border rounded-lg p-4 bg-blue-50">
                  <p className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-2xl">💼</span> LinkedIn (Optional)
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="li_email">LinkedIn Email</Label>
                      <Input
                        id="li_email"
                        type="email"
                        placeholder="your-email@company.com"
                        value={socialCredentials.linkedin_email}
                        onChange={(e) => setSocialCredentials({...socialCredentials, linkedin_email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="li_password">LinkedIn Password</Label>
                      <Input
                        id="li_password"
                        type="password"
                        placeholder="Your LinkedIn password"
                        value={socialCredentials.linkedin_password}
                        onChange={(e) => setSocialCredentials({...socialCredentials, linkedin_password: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Twitter/X */}
                <div className="border rounded-lg p-4 bg-sky-50">
                  <p className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-2xl">𝕏</span> Twitter/X (Optional)
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tw_email">Twitter Email</Label>
                      <Input
                        id="tw_email"
                        type="email"
                        placeholder="your-email@gmail.com"
                        value={socialCredentials.twitter_email}
                        onChange={(e) => setSocialCredentials({...socialCredentials, twitter_email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="tw_password">Twitter Password</Label>
                      <Input
                        id="tw_password"
                        type="password"
                        placeholder="Your Twitter password"
                        value={socialCredentials.twitter_password}
                        onChange={(e) => setSocialCredentials({...socialCredentials, twitter_password: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  disabled={loading}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
              {currentStep < totalSteps ? (
                <Button
                  onClick={() => {
                    if (currentStep === 1) handleStep1Submit();
                    else if (currentStep === 2) handleStep2Submit();
                    else if (currentStep === 3) handleStep3Submit();
                    else if (currentStep === 4) handleStep4Submit();
                    else if (currentStep === 5) handleStep5Submit();
                    else if (currentStep === 6) handleStep6Submit();
                    else if (currentStep === 7) handleStep7Submit();
                    else if (currentStep === 8) handleStep8Submit();
                  }}
                  disabled={loading}
                  className={currentStep === 1 ? 'ml-auto' : ''}
                >
                  {loading ? 'Saving...' : 'Next Step'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleStep9Submit}
                  disabled={loading}
                  className="ml-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  {loading ? 'Activating...' : 'Complete & Activate AI Agent'}
                  <Sparkles className="w-4 h-4 ml-2" />
                </Button>
              )}
              {currentStep === 4 && (
                <Button
                  variant="ghost"
                  onClick={() => setCurrentStep(5)}
                  className="ml-2"
                >
                  Skip Google Integrations
                </Button>
              )}
              {currentStep === 5 && (
                <Button
                  variant="ghost"
                  onClick={() => setCurrentStep(6)}
                  className="ml-2"
                >
                  Skip Social Media
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingWizard;
